import React, { useEffect, useState } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Table, 
  Typography, 
  Alert, 
  Spin, 
  Tag, 
  Button, 
  Avatar, 
  Tooltip,
  Space,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  DollarOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  WarningOutlined,
  LineChartOutlined,
  InboxOutlined,
  BankOutlined,
  SyncOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  TransactionOutlined
} from '@ant-design/icons';
import { useManager } from '../../hooks/useManager';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as ChartTooltip, 
  ResponsiveContainer 
} from 'recharts';

const { Title, Text, Paragraph } = Typography;

const ManagerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getDashboard, loading, error, data } = useManager();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setRefreshing(true);
    await getDashboard();
    setRefreshing(false);
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text className="text-slate-400 animate-pulse uppercase tracking-[0.2em] text-xs">
            {t('common.loading')}
          </Text>
        </Space>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Alert 
          message={t('common.error')} 
          description={error} 
          type="error" 
          showIcon 
          action={
            <Button size="small" danger onClick={loadDashboard}>
              {t('manager.dashboard.refresh') || 'Retry'}
            </Button>
          }
        />
      </div>
    );
  }

  const stats = data?.stats || {};
  
  // Mock data for chart based on recent registrations if needed, 
  // or just a nice trend line for the "Volume" or "Users"
  const chartData = [
    { name: 'Mon', value: 400 },
    { name: 'Tue', value: 300 },
    { name: 'Wed', value: 600 },
    { name: 'Thu', value: 800 },
    { name: 'Fri', value: 500 },
    { name: 'Sat', value: 900 },
    { name: 'Sun', value: stats.new_users_today * 100 || 1000 },
  ];

  const StatCard = ({ title, value, prefix, color, suffix = "" }) => (
    <Card className="trading-card trading-card-hover border-none overflow-hidden h-full">
      <div className="absolute top-0 right-0 p-4 opacity-10 text-4xl">
        {prefix}
      </div>
      <Statistic
        title={<Text className="text-slate-400 text-xs font-bold uppercase tracking-wider">{title}</Text>}
        value={value}
        prefix={<span style={{ color }}>{prefix}</span>}
        suffix={suffix}
        valueStyle={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}
      />
      <div className="mt-2 flex items-center gap-1">
        <Tag color="success" className="m-0 text-[10px] border-none bg-emerald-500/10 text-emerald-400 font-bold">
          +12%
        </Tag>
        <Text className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter">vs last week</Text>
      </div>
    </Card>
  );

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-950 text-slate-200 animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-8 bg-indigo-500 rounded-full" />
            <Title level={2} className="m-0 !text-white !font-black uppercase tracking-tight">
              {t('manager.dashboard.title')}
            </Title>
          </div>
          <Text className="text-slate-500 font-medium tracking-wide">
            {t('manager.dashboard.subtitle')}
          </Text>
        </div>
        <Button 
          type="primary" 
          icon={<SyncOutlined spin={refreshing} />} 
          onClick={loadDashboard}
          className="btn-pro btn-primary flex items-center"
        >
          {refreshing ? t('common.syncing') : (t('manager.dashboard.refresh') || 'Refresh')}
        </Button>
      </div>

      {/* Stats Grid */}
      <Row gutter={[16, 16]} className="mb-8">
        <Col xs={24} sm={12} lg={6}>
          <StatCard 
            title={t('manager.dashboard.stats.new_users')}
            value={stats.new_users_today || 0}
            prefix={<UserOutlined />}
            color="#6366f1"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard 
            title={t('manager.dashboard.stats.pending_kyc')}
            value={stats.pending_kyc || 0}
            prefix={<SafetyCertificateOutlined />}
            color="#f59e0b"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard 
            title={t('manager.dashboard.stats.pending_withdrawals')}
            value={stats.pending_withdrawals || 0}
            prefix={<TransactionOutlined />}
            color="#f43f5e"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard 
            title={t('manager.dashboard.stats.trade_volume')}
            value={stats.today_trade_volume ? parseFloat(stats.today_trade_volume).toFixed(2) : '0.00'}
            prefix={<LineChartOutlined />}
            color="#10b981"
            suffix="$"
          />
        </Col>
      </Row>

      {/* Main Content Area */}
      <Row gutter={[24, 24]}>
        {/* Chart Section */}
        <Col xs={24} xl={16}>
          <Card 
            className="trading-card border-none h-full"
            title={<Text className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2"><LineChartOutlined className="text-indigo-500" /> Platform Growth Activity</Text>}
          >
            <div style={{ height: 300, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#475569', fontSize: 10 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#475569', fontSize: 10 }}
                  />
                  <ChartTooltip 
                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#6366f1" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            <Divider className="border-white/5 my-6" />
            
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic 
                  title={<Text className="text-slate-500 text-[10px] uppercase font-black">Active Inv.</Text>}
                  value={stats.active_investments_count || 0}
                  valueStyle={{ color: '#fff', fontSize: '1.2rem' }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title={<Text className="text-slate-500 text-[10px] uppercase font-black">Active Amount</Text>}
                  value={stats.active_investments_amount || 0}
                  prefix="$"
                  valueStyle={{ color: '#f59e0b', fontSize: '1.2rem' }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title={<Text className="text-slate-500 text-[10px] uppercase font-black">Open Tickets</Text>}
                  value={stats.open_tickets || 0}
                  valueStyle={{ color: '#8b5cf6', fontSize: '1.2rem' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Priority Actions */}
        <Col xs={24} xl={8}>
          <Space direction="vertical" className="w-full" size={24}>
            {/* Pending KYC Card */}
            <Card 
              className="trading-card border-none"
              title={<Text className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2"><SafetyCertificateOutlined className="text-amber-500" /> {t('manager.dashboard.sections.pending_kyc')}</Text>}
              extra={<Link to="/manager/kyc" className="text-indigo-400 text-xs font-bold flex items-center gap-1 hover:text-indigo-300 transition-colors uppercase tracking-widest">{t('manager.dashboard.sections.view_all')} <ArrowRightOutlined /></Link>}
            >
              {data?.pending_kyc && data.pending_kyc.length > 0 ? (
                <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                  {data.pending_kyc.map((kyc) => (
                    <div key={kyc.id} className="flex items-center justify-between p-3 mb-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors border border-white/5 group">
                      <div className="flex items-center gap-3">
                        <Avatar className="bg-amber-500/20 text-amber-500 border border-amber-500/30">
                          {kyc.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                          <div className="text-xs font-bold text-white group-hover:text-indigo-400 transition-colors">{kyc.username}</div>
                          <div className="text-[10px] text-slate-500">{kyc.country || 'Unknown'}</div>
                        </div>
                      </div>
                      <Button 
                        size="small" 
                        type="link" 
                        icon={<EyeOutlined />} 
                        onClick={() => navigate(`/manager/kyc/${kyc.id}`)}
                        className="text-amber-500 hover:text-amber-400"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center bg-white/5 rounded-2xl border border-dashed border-white/10">
                  <InboxOutlined className="text-4xl text-slate-700 mb-2" />
                  <div className="text-slate-500 text-xs font-bold uppercase tracking-widest">No pending verifications</div>
                </div>
              )}
            </Card>

            {/* Pending Withdrawals Card */}
            <Card 
              className="trading-card border-none"
              title={<Text className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2"><TransactionOutlined className="text-rose-500" /> {t('manager.dashboard.sections.pending_withdrawals')}</Text>}
              extra={<Link to="/manager/withdrawals" className="text-indigo-400 text-xs font-bold flex items-center gap-1 hover:text-indigo-300 transition-colors uppercase tracking-widest">{t('manager.dashboard.sections.view_all')} <ArrowRightOutlined /></Link>}
            >
              {data?.pending_withdrawals && data.pending_withdrawals.length > 0 ? (
                <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                  {data.pending_withdrawals.map((w) => (
                    <div key={w.id} className="flex items-center justify-between p-3 mb-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors border border-white/5 group">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500">
                          <DollarOutlined />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white">{w.username}</div>
                          <div className="text-[10px] font-black text-rose-400 uppercase tracking-widest">${parseFloat(w.amount).toFixed(2)}</div>
                        </div>
                      </div>
                      <Button 
                        size="small" 
                        type="link" 
                        onClick={() => navigate(`/manager/withdrawals/${w.id}`)}
                        className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]"
                      >
                        {t('manager.dashboard.table.process')}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center bg-white/5 rounded-2xl border border-dashed border-white/10">
                  <InboxOutlined className="text-4xl text-slate-700 mb-2" />
                  <div className="text-slate-500 text-xs font-bold uppercase tracking-widest">Clean withdrawal queue</div>
                </div>
              )}
            </Card>
          </Space>
        </Col>
      </Row>

      {/* Recent Users Table */}
      <Card 
        className="trading-card border-none mt-8"
        title={<Text className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2"><UserOutlined className="text-indigo-500" /> {t('manager.dashboard.sections.recent_users')}</Text>}
      >
        <Table
          dataSource={data?.recent_users}
          rowKey="id"
          pagination={false}
          size="small"
          className="custom-table"
          scroll={{ x: 'max-content' }}
          columns={[
            { 
              title: t('manager.dashboard.table.user'), 
              dataIndex: 'username',
              render: (text, record) => (
                <Space>
                  <Avatar size="small" className="bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                    {text.charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <div className="text-xs font-bold text-white">{text}</div>
                    <div className="text-[10px] text-slate-500">{record.email}</div>
                  </div>
                </Space>
              )
            },
            { 
              title: t('manager.dashboard.table.full_name'), 
              dataIndex: 'full_name',
              render: (text) => <Text className="text-slate-400 text-xs">{text || 'N/A'}</Text>
            },
            { 
              title: t('manager.dashboard.table.country'), 
              dataIndex: 'country',
              render: (text) => <Tag className="m-0 border-none bg-slate-800 text-slate-400 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">{text || 'Unknown'}</Tag>
            },
            { 
              title: t('manager.dashboard.table.kyc'), 
              dataIndex: 'kyc_status',
              render: (status) => {
                const colors = { pending: 'orange', verified: 'green', rejected: 'red' };
                const labels = { 
                  pending: t('admin.users_management.table.kyc.pending'), 
                  verified: t('admin.users_management.table.kyc.verified'), 
                  rejected: t('admin.users_management.table.kyc.rejected') 
                };
                return <Tag color={colors[status]} className="m-0 text-[10px] font-bold uppercase tracking-widest">{labels[status] || status}</Tag>;
              }
            },
            { 
              title: t('manager.dashboard.table.registered_on'), 
              dataIndex: 'created_at', 
              render: (date) => <Text className="text-slate-500 text-[10px]">{new Date(date).toLocaleDateString()}</Text>
            },
            {
              title: '',
              key: 'actions',
              align: 'right',
              render: (_, record) => (
                <Tooltip title="View Details">
                  <Button 
                    type="link" 
                    icon={<ArrowRightOutlined />} 
                    className="text-indigo-400 hover:text-indigo-300"
                    onClick={() => navigate(`/admin/users/${record.id}`)}
                  />
                </Tooltip>
              )
            }
          ]}
        />
      </Card>

      <style jsx="true">{`
        .custom-table .ant-table {
          background: transparent !important;
        }
        .custom-table .ant-table-thead > tr > th {
          background: transparent !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          color: #475569 !important;
          font-size: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.1em !important;
          font-weight: 900 !important;
          padding: 12px 16px !important;
        }
        .custom-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
          padding: 12px 16px !important;
        }
        .custom-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.02) !important;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(99, 102, 241, 0.2);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default ManagerDashboard;